// Body-region silhouette paths, one set per audience, ported verbatim from
// the source prototype's positionZones() function (see Phase 1 report §4 —
// "reuse, don't reinvent"). Coordinates are in a 0–100 x 0–150 viewBox that
// lines up with the coded avatar silhouettes in avatarSilhouettes.ts.
//
// These are real body-aligned silhouette masks, not floating shapes — do
// not replace with rectangles/polygons per the spec's explicit prohibition.

import type { Audience } from '@/types/domain';

export const AVATAR_VIEWBOX = '0 0 100 150';

export type BodyRegionKey =
  | 'Shoulders'
  | 'Chest'
  | 'ArmL'
  | 'ArmR'
  | 'Waist'
  | 'Hip'
  | 'ThighL'
  | 'ThighR';

export const BODY_REGION_ORDER: BodyRegionKey[] = [
  'Shoulders',
  'Chest',
  'ArmL',
  'ArmR',
  'Waist',
  'Hip',
  'ThighL',
  'ThighR',
];

export const AVATAR_REGION_PATHS: Record<Exclude<Audience, 'child'> | 'child', Record<BodyRegionKey, string>> = {
  women: {
    Shoulders: 'M37 29 C41 27,59 27,63 29 L62 33 C56 34,44 34,38 33 Z',
    Chest: 'M36 33 C41 31,59 31,64 33 L63 47 C58 51,42 51,37 47 Z',
    ArmL: 'M33 35 C30 42,28 50,27 60 C26 69,26 76,27 82 L30 82 C31 76,31.5 68,32.5 59 C33.5 49,34.5 42,36 36 Z',
    ArmR: 'M67 35 C70 42,72 50,73 60 C74 69,74 76,73 82 L70 82 C69 76,68.5 68,67.5 59 C66.5 49,65.5 42,64 36 Z',
    Waist: 'M41 48 C45 46.5,55 46.5,59 48 L58 63 C54.5 65.5,45.5 65.5,42 63 Z',
    Hip: 'M39 64 C44 62.5,56 62.5,61 64 L60 79 C55.5 82,44.5 82,40 79 Z',
    ThighL: 'M43 79 C45 78.5,47 78.5,48.5 79.5 L48 109 C46.5 111.5,44.5 111.5,43 109 Z',
    ThighR: 'M51.5 79.5 C53 78.5,55 78.5,57 79 L57 109 C55.5 111.5,53.5 111.5,52 109 Z',
  },
  men: {
    Shoulders: 'M34 28 C39 26,61 26,66 28 L65 33 C58 34,42 34,35 33 Z',
    Chest: 'M34 32 C40 30,60 30,66 32 L65 47 C59 50.5,41 50.5,35 47 Z',
    ArmL: 'M31 35 C28 43,26 52,25 62 C24 71,24 78,25 84 L28.5 84 C29.5 78,30.5 70,31.5 61 C32.5 51,33.5 43,35 36 Z',
    ArmR: 'M69 35 C72 43,74 52,75 62 C76 71,76 78,75 84 L71.5 84 C70.5 78,69.5 70,68.5 61 C67.5 51,66.5 43,65 36 Z',
    Waist: 'M40 47 C45 45.5,55 45.5,60 47 L59 62 C55 64.5,45 64.5,41 62 Z',
    Hip: 'M38 62.5 C43 61,57 61,62 62.5 L61 78 C56 80.5,44 80.5,39 78 Z',
    ThighL: 'M43 78 C45 77.5,47 77.5,49 78.5 L48.5 110 C47 112.5,45 112.5,43.5 110 Z',
    ThighR: 'M51 78.5 C53 77.5,55 77.5,57 78 L56.5 110 C55 112.5,53 112.5,51.5 110 Z',
  },
  child: {
    Shoulders: 'M38 37 C42 35,58 35,62 37 L61 41 C56 42,44 42,39 41 Z',
    Chest: 'M37 41 C42 39.5,58 39.5,63 41 L62 55 C57 57.5,43 57.5,38 55 Z',
    ArmL: 'M34 42 C30 47,27 54,25 62 C24 69,24 75,25 80 L28 80 C29 74.5,29.5 68,30.5 61 C31.5 53,32.5 47,35 43 Z',
    ArmR: 'M66 42 C70 47,73 54,75 62 C76 69,76 75,75 80 L72 80 C71 74.5,70.5 68,69.5 61 C68.5 53,67.5 47,65 43 Z',
    Waist: 'M42 56 C45.5 54.5,54.5 54.5,58 56 L57 69 C54 71,46 71,43 69 Z',
    Hip: 'M40 69 C44 67.5,56 67.5,60 69 L59 83 C55 85.5,45 85.5,41 83 Z',
    ThighL: 'M44 83 C45.5 82.5,47.5 82.5,49 83.2 L48.5 107 C47 109,45.5 109,44 107 Z',
    ThighR: 'M51 83.2 C52.5 82.5,54.5 82.5,56 83 L56 107 C54.5 109,53 109,51.5 107 Z',
  },
};

/** Which regions belong to each measurement zone (chest/waist/hip), used to
 * decide fill colour per region from a FitZoneState. */
export const REGION_TO_METRIC: Record<BodyRegionKey, 'chest' | 'waist' | 'hip'> = {
  Shoulders: 'chest',
  Chest: 'chest',
  ArmL: 'chest',
  ArmR: 'chest',
  Waist: 'waist',
  Hip: 'hip',
  ThighL: 'hip',
  ThighR: 'hip',
};
